/**
 * ThresholdBandBar - the threshold control in a card's Assurance Rules tab.
 *
 * A card stores four thresholds plus a null catch-all, but what those numbers
 * mean is five contiguous bands. The old control was five bare inputs, so
 * `High: 98` read as "High equals 98" rather than "High starts at 98", and
 * nothing showed that 98/95/90/85 carves 100 points into a 2-point High band
 * and an 85-point None one.
 *
 * So: a proportional bar showing the bands at true scale, then one row per
 * band carrying its input and the range it currently covers. The bar is the
 * picture, the rows are the editor.
 *
 * `deviation_bands` draws the bar mirrored around the base, because that mode
 * is a two-sided tolerance. The backend classifies on the absolute deviation,
 * so the axis it computes on is one-sided and a bar drawn on it would imply
 * `<= 3` - a ceiling - rather than "within +/-3%".
 */
import { useEffect, useRef, useState } from 'react';
import InputField from 'src/components/ui/Input';
import type { BoundaryDirection, DataValueType, Level, LevelCalculationMode } from '../api/types';
import { dashboardAssuranceColors, getTextColorForBg, type AssuranceLevel } from '../utils/color';
import { parseThresholdInput } from '../utils/validation';
import {
  applyThresholdCut,
  bandRangeText,
  BAND_LABELS,
  CUT_LEVELS,
  formatBandNumber,
  getBandAxis,
  getBandSpans,
  getDrawnSegments,
  type BandConfig
} from '../utils/assuranceBands';
import { useChartTilesEnabled } from '../hooks/useDashboardTileStyle';

interface ThresholdBandBarProps {
  thresholds: Record<Level, string>;
  onChange: (next: Record<Level, string>) => void;
  mode: LevelCalculationMode;
  direction: BoundaryDirection;
  dataValueType: DataValueType;
  /** Levels whose stored threshold failed validation, highlighted on their field. */
  errorLevels?: Level[];
  disabled?: boolean;
}

/** Minimum gap between two tick labels, as a percentage of bar width. */
const TICK_SPACING = 9;

/** Rough advance width of the band labels at 11px semibold, plus their breathing room. */
const APPROX_CHAR_PX = 7;
const LABEL_PADDING_PX = 8;

/** `Level` is lower case; the colour maps are keyed by display case. */
function toAssuranceLevel(level: Level): AssuranceLevel {
  return (level.charAt(0).toUpperCase() + level.slice(1)) as AssuranceLevel;
}

export default function ThresholdBandBar({
  thresholds,
  onChange,
  mode,
  direction,
  dataValueType,
  errorLevels = [],
  disabled = false
}: ThresholdBandBarProps) {
  const useChartTiles = useChartTilesEnabled();
  const palette = dashboardAssuranceColors(useChartTiles);
  const colorFor = (level: Level) => palette[toAssuranceLevel(level)];

  const barRef = useRef<HTMLDivElement>(null);
  const [barWidth, setBarWidth] = useState(0);
  const [nudge, setNudge] = useState<string | null>(null);

  const config: BandConfig = { thresholds, mode, direction, dataValueType };
  const axis = getBandAxis(config);
  const spans = getBandSpans(config);

  // A mirrored bar spans -max..+max, so it is twice as wide as the axis.
  const drawnSpan = axis.mirrored ? axis.max * 2 : Math.abs(axis.max - axis.min) || 1;

  // Left is lower, right is higher (High flips with direction). Close-to-base
  // stays mirrored around the centre.
  const segments = getDrawnSegments(config);
  const totalSize = segments.reduce((sum, segment) => sum + segment.size, 0) || 1;

  // Whether a band's name fits inside it depends on pixels, not percentages -
  // the same 12% band is legible in a wide modal and not in a narrow pane - so
  // measure the bar rather than guessing from the proportion.
  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return undefined;
    const measure = () => setBarWidth(bar.getBoundingClientRect().width);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(bar);
    return () => observer.disconnect();
  }, []);

  const commit = (level: Level, rawValue: string) => {
    const cleaned = rawValue.replace(/[^0-9.-]/g, '');
    if (parseThresholdInput(cleaned) === null) {
      // Let a half-typed value ('', '-', '1.') sit in the field untouched.
      onChange({ ...thresholds, [level]: cleaned });
      setNudge(null);
      return;
    }
    const result = applyThresholdCut(thresholds, level, cleaned, axis.ascending);
    onChange(result.thresholds);
    setNudge(
      result.moved.length > 0
        ? `${result.moved.join(' and ')} moved to ` +
            `${formatBandNumber(parseThresholdInput(result.thresholds[level]), axis.dp)}${axis.unit} ` +
            'to keep the bands in order.'
        : null
    );
  };

  // Ticks sit on the cuts. A mirrored bar carries +/- on the right half only:
  // signed labels on both sides collide at panel width, and dropping some of
  // them would make a symmetric picture look lopsided. Its centre needs no
  // label - the rule marks it and the under/over row underneath names it.
  let ticks: { at: number; text: string; anchored?: boolean }[];
  if (axis.mirrored) {
    const candidates = CUT_LEVELS.map((level) => {
      const value = parseThresholdInput(thresholds[level]);
      return value === null
        ? null
        : { at: 50 + (value / drawnSpan) * 100, text: `±${formatBandNumber(value, axis.dp)}%` };
    }).filter((tick): tick is { at: number; text: string } => tick !== null);
    // Always keep the first - it is the High boundary, the one the rule is
    // usually described by - and the last, then whatever fits between.
    ticks = [];
    let previous = -Infinity;
    candidates.forEach((tick, index) => {
      if (index === 0 || index === candidates.length - 1 || tick.at - previous >= TICK_SPACING) {
        ticks.push(tick);
        previous = tick.at;
      }
    });
  } else {
    // Number line: 0% is axis.min (left), 100% is axis.max (right).
    const atPct = (value: number) => ((value - axis.min) / drawnSpan) * 100;
    const cuts = CUT_LEVELS.map((level) => {
      const value = parseThresholdInput(thresholds[level]);
      return value === null ? null : { at: atPct(value), text: `${formatBandNumber(value, axis.dp)}${axis.unit}` };
    }).filter((tick): tick is { at: number; text: string } => tick !== null);
    const candidates = [
      { at: 0, text: `${formatBandNumber(axis.min, axis.dp)}${axis.unit}`, anchored: true },
      ...cuts.sort((a, b) => a.at - b.at)
    ];
    let previous = -Infinity;
    ticks = candidates.filter((tick) => {
      if (tick.at - previous >= TICK_SPACING) {
        previous = tick.at;
        return true;
      }
      return false;
    });
    const last = candidates[candidates.length - 1];
    if (ticks[ticks.length - 1] !== last) {
      while (ticks.length > 0 && last.at - ticks[ticks.length - 1].at < TICK_SPACING) ticks.pop();
      ticks.push(last);
    }
  }

  return (
    <div className="threshold-inputs">
      <div className="relative">
        <div ref={barRef} className="flex h-7 overflow-hidden rounded-md">
          {segments.map((segment, index) => {
            const percent = (segment.size / totalSize) * 100;
            const background = colorFor(segment.level);
            const label = BAND_LABELS[segment.level];
            const availablePx = (percent / 100) * barWidth - LABEL_PADDING_PX;
            const abbreviated = label.slice(0, 3);
            const fittedLabel =
              availablePx >= label.length * APPROX_CHAR_PX
                ? label
                : availablePx >= abbreviated.length * APPROX_CHAR_PX
                  ? abbreviated
                  : '';
            return (
              <div
                key={`${segment.level}-${index}`}
                className="flex items-center justify-center overflow-hidden text-[11px] font-semibold whitespace-nowrap"
                style={{
                  width: `${percent.toFixed(2)}%`,
                  backgroundColor: background,
                  color: getTextColorForBg(background)
                }}
                title={`${BAND_LABELS[segment.level]} · ${bandRangeText(config, spans.find((span) => span.level === segment.level)!)}`}
              >
                {fittedLabel}
              </div>
            );
          })}
          {/* The base sits at the centre of the High band, so a full-height
              rule would cut its label in half. Mark it with a stub at each
              edge instead and leave the middle clear. */}
          {axis.mirrored && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 left-1/2 -ml-px w-0.5 bg-gradient-to-b from-white via-transparent to-white"
            />
          )}
        </div>
      </div>

      <div className="relative mt-1 h-4">
        {ticks.map((tick) => (
          <span
            key={`${tick.at}-${tick.text}`}
            className="absolute -translate-x-1/2 font-mono text-[10px] whitespace-nowrap tabular-nums text-content-subtlest"
            style={{ left: `${tick.at.toFixed(2)}%`, transform: tick.anchored ? 'none' : undefined }}
          >
            {tick.text}
          </span>
        ))}
      </div>

      {axis.mirrored && (
        <div className="flex justify-between text-[10px] text-content-subtlest">
          <span>← under base</span>
          <span>over base →</span>
        </div>
      )}

      <div className="mt-3 space-y-2">
        {spans.map((span) => {
          const isCatchAll = span.level === 'none';
          const hasError = errorLevels.includes(span.level);
          return (
            <div key={span.level} className="flex items-center gap-3">
              <span
                aria-hidden="true"
                className="h-6 w-1.5 shrink-0 rounded-sm"
                style={{ backgroundColor: colorFor(span.level) }}
              />
              <label
                htmlFor={isCatchAll ? undefined : `threshold-${span.level}`}
                className="w-24 shrink-0 text-sm font-semibold text-content-subtle"
              >
                {BAND_LABELS[span.level]}
              </label>
              {isCatchAll ? (
                // The catch-all takes no input: anything that didn't qualify
                // for a band above lands here, and its saved threshold is
                // always null - see
                // calcard_repositories._get_assurance_level_from_context.
                <span className="min-w-0 flex-1 text-sm text-content-subtlest italic">Everything left over</span>
              ) : (
                <InputField
                  id={`threshold-${span.level}`}
                  type="text"
                  inputMode="decimal"
                  disabled={disabled}
                  value={thresholds[span.level]}
                  onChange={(event) => commit(span.level, event.target.value)}
                  state={hasError ? 'error' : 'default'}
                  fullWidth
                  className="min-w-0 flex-1"
                  wrapperClassName={hasError ? 'focus-within:ring-2 focus-within:ring-misc-error/40' : undefined}
                  inputClassName="text-right font-mono tabular-nums"
                />
              )}
              <span
                className={`w-24 shrink-0 text-right font-mono text-xs tabular-nums ${
                  hasError ? 'text-misc-error' : 'text-content-subtlest'
                }`}
              >
                {bandRangeText(config, span)}
              </span>
            </div>
          );
        })}
      </div>

      {nudge && <p className="mt-2 text-xs text-content-active">{nudge}</p>}
    </div>
  );
}
